import { Link } from "react-router-dom";
import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import BrandLogo from "@/components/site/BrandLogo";

export default function AuthShell({ eyebrow, title, subtitle, children, footer }) {
  return (
    <div className="min-h-screen flex bg-brand-ink text-white">
      {/* Left visual */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-gradient" />
        <div className="absolute inset-0 bg-grid-overlay opacity-40" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Link to="/" className="flex items-center gap-3">
            <BrandLogo variant="wordmark-dark" className="h-12 w-auto rounded-md" />
          </Link>
          <div>
            <p className="label-eyebrow">One App · One Wallet · One Africa</p>
            <h1 className="mt-4 font-display font-black text-4xl text-balance">
              Call home for less.<br />
              Move money <span className="text-brand-green">faster</span>.
            </h1>
            <p className="mt-5 text-white/65 max-w-md">
              Join the diaspora platform built for trustworthy international calling, instant top-ups and secure money transfer.
            </p>
          </div>
          <div className="text-xs uppercase tracking-[0.22em] text-white/45">
            Operated by Scitygate Telecom UK Ltd · Manchester, UK
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 bg-white text-brand-ink">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-3 mb-8">
            <BrandLogo variant="wordmark" className="h-10 w-auto" />
          </Link>
          <p className="label-eyebrow">{eyebrow}</p>
          <h2 className="mt-3 font-display font-black text-3xl">{title}</h2>
          {subtitle && <p className="mt-2 text-sm text-brand-muted">{subtitle}</p>}
          <div className="mt-8">{children}</div>
          {footer && <div className="mt-6 text-sm text-brand-muted">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

export function Field({ label, type = "text", value, onChange, error, placeholder, required, testId, autoComplete, name }) {
  const [show, setShow] = useState(false);
  const isPassword = type === "password";
  const effectiveType = isPassword && show ? "text" : type;
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.22em] font-semibold mb-2">
        {label} {required && <span className="text-brand-green">*</span>}
      </label>
      <div className="relative">
        <input
          type={effectiveType}
          name={name}
          autoComplete={autoComplete}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          data-testid={testId}
          className={`w-full rounded-md border bg-white px-4 ${isPassword ? "pr-12" : ""} py-3 text-sm placeholder:text-brand-muted/70 focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green ${
            error ? "border-red-400" : "border-brand-line"
          }`}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow((v) => !v)}
            data-testid={testId ? `${testId}-toggle` : "password-toggle"}
            aria-label={show ? "Hide password" : "Show password"}
            tabIndex={-1}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-brand-muted hover:text-brand-ink"
          >
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        )}
      </div>
      {error && <p className="mt-1.5 text-xs text-red-600" data-testid={`${testId}-error`}>{error}</p>}
    </div>
  );
}
